
import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileManagementService } from '../../../../../Component/Api/FileManagement/FileManagementService';
import { DataTable } from '../../../../../Component/Common/Components/DataTable/DataTable';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import { NoDataScreen } from '../../../../../Component/Common/Components/NoDataScreen/NoDataScreen';
import { ColumnSortOption } from '../../../../../Component/Model/Dto/ColumnSortOption';
import { FileManagementImportSearchDto, FileManagementSearchInput } from '../../../../../Component/Model/Dto/FileManagement';
import { EntriesPerPage } from '../../../../../Component/Model/Table/Table';
import { isNullOrEmpty } from '../../../../../Component/Model/helpers';
import { FileManagementImportModal } from './FileManagementImportModal';
import { FileManagmentImportTableMapper } from './FileManagementImportTableMapper';

interface IProps {
    scorecardId: string;
}

export const FileManagementImport = (props: IProps) => {

    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 5 });
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [isLoading, setIsLoading] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState<string>("");
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [data, setData] = React.useState<FileManagementImportSearchDto[]>([]);
    const [counter, setCounter] = React.useState(0);

    const [selectedImportId, setSelectedImportId] = React.useState(-1);
    const [showModal, setShowModal] = React.useState(false);

    const searchInput: FileManagementSearchInput = {
        scorecardId: props.scorecardId,
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        columnSortOptions: columnSortOptions
    }

    const downloadFile = async (id: number) => {
        try {
            const response = await FileManagementService.ImportDownload(id);
            window.open(response.data);
        } catch (error) {
            console.log(error);
        }
    }


    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await FileManagementService.ImportSearch(searchInput);
            setTotalItemsCount(response.data.totalItemsCount);
            setFilteredItemsCount(response.data.filteredItemsCount);
            setData(response.data.results);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
            setCounter(counter + 1);
        }
    }


    const openImportModal = (id: number) => {
        setSelectedImportId(id);
        setShowModal(true);
    }

    const closeImportModal = () => {
        setShowModal(false);
        setSelectedImportId(-1);
    }

    useEffect(() => {
        if (!isNullOrEmpty(props.scorecardId)) {
            fetchData();
        }
    }, [props.scorecardId, searchValue, entriesPerPage, pageNumber, columnSortOptions]);

    const tableData = FileManagmentImportTableMapper(data, downloadFile, openImportModal);

    return (
        <>
            {counter === 0 && <SmallLoading />}
            {counter > 0 && totalItemsCount > 0 &&
                <>
                    <Box style={{ paddingTop: '10px' }}>
                        <Typography variant='body2' color='secondary'>
                            The following files have been imported into the system.
                        </Typography>
                    </Box>
                    <DataTable
                        key={totalItemsCount}
                        table={tableData!}
                        entriesPerPage={entriesPerPage}
                        pageNumber={pageNumber}
                        filteredItemsCount={filteredItemsCount}
                        totalItemsCount={totalItemsCount}
                        entriesPerPageChanged={entriesPerPageChanged}
                        pageNumberChanged={pageNumberChanged}
                        columnSortingChanged={columnSortingChanged}
                        searchValueChanged={searchValueChanged}
                        isLoading={isLoading}
                        manualSorting={true}
                        canSearch={true}
                    />

                    {selectedImportId > 0 && showModal && <FileManagementImportModal showModal={showModal} id={selectedImportId} closeModal={closeImportModal} />}
                </>
            }
            {counter > 0 && totalItemsCount === 0 &&
                <NoDataScreen
                    subText1='No files have been imported yet.'
                    subText3={
                        <Typography variant="body2" color="secondary">
                            Please visit the Import Data page <Link to={`/bee/scorecard/${props.scorecardId}/importPage`}>here</Link> to start importing files.
                        </Typography>
                    } />
            }
        </>
    )

}
